import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../Auth/AuthContext";
import { Send, X, AlertTriangle, Minimize, Maximize } from 'lucide-react';
import IconChatbot from "../../assets/Icons/IconChatbot.png";

// API URL configuration that automatically selects the appropriate backend
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  
  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:5004';
  }
  
  // Firebase hosting / staging
  if (hostname === 'gigify3.web.app' || hostname === 'gigify3.firebaseapp.com') {
    return 'https://gigify-chatbot-api-new-1e4dcaa62677.herokuapp.com'; 
  }
  
  // Production domain
  if (hostname === 'www.gigify.co.za' || hostname === 'gigify.co.za') {
    return 'https://gigify.co.za/api'; 
  }
  
  // Default fallback
  return 'https://gigify.co.za/api';
};

const API_BASE_URL = getApiBaseUrl();

const ChatBot = ({ isOpenExternal = false, onCloseExternal = null }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [messages, setMessages] = useState(() => {
        // Attempt to load persisted messages from localStorage
        const savedMessages = localStorage.getItem('gigify-chat-messages');
        return savedMessages ? JSON.parse(savedMessages) : [
            { text: "Hi! I'm GiGBot. How can I help you today?", sender: "bot", timestamp: new Date().toISOString() }
        ];
    });
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [streamingMessage, setStreamingMessage] = useState("");
    const [processingStatus, setProcessingStatus] = useState("");
    // Position state with default in bottom right
    const [position, setPosition] = useState(() => {
        // Attempt to load persisted position from localStorage
        const savedPosition = localStorage.getItem('gigify-chat-position');
        return savedPosition ? JSON.parse(savedPosition) : { right: 40, bottom: 40 };
    });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [isMobile, setIsMobile] = useState(false);
    
    const messagesEndRef = useRef(null);
    const { currentUser } = useAuth();
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    
    // Track active API requests for cleanup
    const activeRequestRef = useRef(null);
    const streamSource = useRef(null);
    
    // Store the user ID to maintain consistent identity
    const userIdRef = useRef(currentUser?.uid || "guest-" + Math.random().toString(36).substr(2, 9));
    
    // Check if the device is mobile
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkIfMobile(); // Check initially
        window.addEventListener('resize', checkIfMobile); // Add listener for resize events
        
        return () => {
            window.removeEventListener('resize', checkIfMobile); // Clean up
        };
    }, []);
    
    // Handle external control for mobile
    useEffect(() => {
        if (isMobile && isOpenExternal !== undefined) {
            setIsOpen(isOpenExternal);
        }
    }, [isOpenExternal, isMobile]);

    // Handle closing from mobile
    const handleMobileClose = () => {
        setIsOpen(false);
        if (onCloseExternal) {
            onCloseExternal();
        }
    };
    
    // Persist messages when they change
    useEffect(() => {
        localStorage.setItem('gigify-chat-messages', JSON.stringify(messages.slice(-50))); // Keep last 50 messages
    }, [messages]);
    
    // Persist position when it changes
    useEffect(() => {
        localStorage.setItem('gigify-chat-position', JSON.stringify(position));
    }, [position]);

    // Focus input field when chat window opens
    useEffect(() => {
        if (isOpen && !isMinimized && inputRef.current) {
            setTimeout(() => {
                inputRef.current.focus();
            }, 300); // Short delay to allow animation to complete
        }
        
        // Cleanup function to abort any ongoing requests when component unmounts
        return () => {
            if (activeRequestRef.current) {
                activeRequestRef.current.abort();
            }
            if (streamSource.current) {
                streamSource.current.close();
                streamSource.current = null;
            }
        };
    }, [isOpen, isMinimized]);

    // Handle pointer events for both mouse and touch
    useEffect(() => {
        const handlePointerMove = (e) => {
            if (isDragging) {
                // Calculate the movement
                const deltaX = e.clientX - dragStart.x;
                const deltaY = e.clientY - dragStart.y;
                
                // Calculate new position
                const newRight = Math.max(0, position.right - deltaX);
                const newBottom = Math.max(0, position.bottom - deltaY);
                
                // Get viewport dimensions
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                
                // Check if we're near edges for snapping (within 20px of edge)
                const snapThreshold = 20;
                let finalRight = newRight;
                let finalBottom = newBottom;
                
                // Snap to right edge
                if (newRight < snapThreshold) {
                    finalRight = 0;
                }
                
                // Snap to bottom edge
                if (newBottom < snapThreshold) {
                    finalBottom = 0;
                }
                
                // Apply boundary constraints to keep the bot within the viewport
                const maxRight = viewportWidth - 80; // Approximate min width of the bot
                const maxBottom = viewportHeight - 80; // Approximate min height of the bot
                
                setPosition({
                    right: Math.min(finalRight, maxRight),
                    bottom: Math.min(finalBottom, maxBottom)
                });
                
                // Update drag start position for next movement
                setDragStart({
                    x: e.clientX,
                    y: e.clientY
                });
                
                // Prevent default behavior to avoid scrolling on touch devices
                e.preventDefault();
            }
        };
        
        const handlePointerUp = () => {
            setIsDragging(false);
        };
        
        if (isDragging) {
            // Use pointer events instead of mouse events to support both mouse and touch
            document.addEventListener('pointermove', handlePointerMove);
            document.addEventListener('pointerup', handlePointerUp);
        }
        
        return () => {
            document.removeEventListener('pointermove', handlePointerMove);
            document.removeEventListener('pointerup', handlePointerUp);
        };
    }, [isDragging, dragStart, position]);

    // Handler to start dragging
    const handlePointerDown = (e) => {
        // For mobile, don't enable dragging
        if (isMobile) return;
        
        // Prevent default behavior to avoid text selection during drag
        e.preventDefault();
        
        setIsDragging(true);
        setDragStart({
            x: e.clientX,
            y: e.clientY
        });
    };

    // Animation class for messages
    const messageAnimation = {
        animation: 'fadeInSlide 0.3s ease-out forwards'
    };

    // Function to format timestamps
    const formatTime = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Styles object with updated design
    const styles = {
        container: {
            position: "fixed",
            bottom: `${position.bottom}px`,
            right: `${position.right}px`,
            zIndex: 9999,
            fontFamily: "Montserrat, sans-serif",
            cursor: isDragging ? 'grabbing' : 'grab'
        },
        mobileContainer: {
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 9999,
            fontFamily: "Montserrat, sans-serif"
        },
        toggleContainer: {
            position: "absolute",
            bottom: "0",
            right: "0",
            display: "flex",
            alignItems: "center",
            gap: "20px"
        },
        toggleButton: {
            width: "80px", // Increased size more
            height: "80px", // Increased size more
            backgroundColor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isDragging ? 'grabbing' : 'pointer',
            border: "none",
            transition: "all 0.2s ease",
            position: "relative",
            overflow: "visible" // Changed from hidden to visible to prevent cutting off the icon
        },
        // Mobile toggle button - smaller and more compact
        mobileToggleButton: {
            width: "80px",
            height: "80px",
            backgroundColor: "transparent",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            border: "none",
            transition: "all 0.3s ease",
            position: "relative",
            overflow: "visible" // Added to prevent cutting off the icon
        },
        toggleButtonInner: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            padding: "3px" // Added padding to prevent edges from being cut off
        },
        chatbotIcon: {
            width: "70px", // Increased size more
            height: "70px", // Increased size more
            transition: "transform 0.2s ease",
            objectFit: "contain", // Ensures the image maintains its aspect ratio
            animation: "pulse 2s infinite" // Added pulsing animation
        },
        mobileChatbotIcon: {
            width: "60px", // Increased from 40px to 60px
            height: "60px", // Increased from 40px to 60px
            transition: "transform 0.2s ease",
            objectFit: "contain",
            animation: "pulse 2s infinite" // Added pulsing animation
        },
        toggleLabel: {
            backgroundColor: "#01a2f1",
            color: "white",
            padding: "12px 16px", // Increased padding
            fontSize: "0.95rem", // Increased font size
            fontWeight: "500",
            boxShadow: "0px 4px 8px rgba(1, 162, 241, 0.3)",
            opacity: isOpen ? 0 : 1,
            transform: isOpen ? "translateX(20px)" : "translateX(0)",
            transition: "all 0.3s ease",
            animation: "pulse 2s infinite",
            maxWidth: "500px", // Added max width
            whiteSpace: "normal", // Allow text wrapping
            textAlign: "center",
            lineHeight: "1.3", // Increased line height
            borderRadius: "5px" // Added border radius
        },
        chatWindow: {
            position: "absolute",
            bottom: "90px", // Adjusted more to accommodate larger icon
            right: "0",
            width: "350px",
            height: isMinimized ? "60px" : "500px",
            backgroundColor: "#fff",
            boxShadow: "0px 5px 25px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            transition: "height 0.3s ease-in-out",
            border: "1px solid rgba(1, 162, 241, 0.2)"
        },
        // Mobile chat window (full screen)
        mobileChatWindow: {
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            width: "100%",
            height: "100%",
            backgroundColor: "#fff",
            zIndex: 10000,
            display: "flex",
            flexDirection: "column",
            transition: "all 0.3s ease-in-out"
        },
        header: {
            background: "linear-gradient(90deg, #01a2f1 0%, #0088d1 100%)",
            padding: "15px 20px",
            color: "white",
            fontWeight: "600",
            fontSize: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            cursor: "grab",
            userSelect: "none" // Prevent text selection during dragging
        },
        mobileHeader: {
            background: "linear-gradient(90deg, #01a2f1 0%, #0088d1 100%)",
            padding: "15px 20px",
            color: "white",
            fontWeight: "600",
            fontSize: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            userSelect: "none" // Prevent text selection
        },
        headerLeft: {
            display: "flex",
            alignItems: "center",
            gap: "10px",
        },
        headerRight: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
        },
        headerIcon: {
            width: "24px",
            height: "24px",
        },
        headerButton: {
            background: "rgba(255, 255, 255, 0.25)", // Increased contrast
            border: "none",
            color: "white",
            cursor: "pointer",
            width: "28px",
            height: "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease",
            "&:hover": {
                background: "rgba(255, 255, 255, 0.4)", // Increased contrast for hover
            }
        },
        closeButton: {
            background: "rgba(255, 255, 255, 0.35)", // Even higher contrast
            border: "none",
            color: "white",
            cursor: "pointer",
            width: "28px",
            height: "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease",
            "&:hover": {
                background: "rgba(255, 255, 255, 0.5)", 
            }
        },
        messagesContainer: {
            flex: 1,
            overflowY: "auto",
            padding: "20px",
            backgroundColor: "#f8f9fa",
            display: isMinimized ? "none" : "block"
        },
        messageGroup: {
            marginBottom: "20px",
            display: "flex",
            flexDirection: "column",
        },
        message: {
            maxWidth: "85%",
            position: "relative",
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            marginBottom: "2px"
        },
        botIcon: {
            width: "36px",
            height: "36px",
            marginTop: "5px"
        },
        userMessage: {
            marginLeft: "auto",
            backgroundColor: "#01a2f1",
            color: "white",
            padding: "12px 16px",
            boxShadow: "0px 2px 5px rgba(1, 162, 241, 0.15)",
            fontSize: "0.95rem",
            whiteSpace: "pre-wrap", // Preserve line breaks
            wordBreak: "break-word" // Ensure long words don't overflow
        },
        botMessage: {
            backgroundColor: "white",
            color: "#144874",
            padding: "12px 16px",
            boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.05)",
            border: "1px solid rgba(1, 162, 241, 0.1)",
            fontSize: "0.95rem",
            whiteSpace: "pre-wrap", // Preserve line breaks
            wordBreak: "break-word" // Ensure long words don't overflow
        },
        errorMessage: {
            backgroundColor: "rgba(255, 99, 71, 0.1)", // Light red background
            color: "#e74c3c",
            padding: "12px 16px",
            boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.05)",
            border: "1px solid rgba(231, 76, 60, 0.3)",
            fontSize: "0.95rem",
            display: "flex",
            alignItems: "center",
            gap: "10px"
        },
        timestamp: {
            fontSize: "0.7rem",
            color: "#999",
            marginTop: "4px",
            alignSelf: "flex-end"
        },
        quickResponse: {
            padding: "8px 12px",
            margin: "5px 5px 5px 0",
            backgroundColor: "white",
            border: "1px solid #01a2f1",
            color: "#01a2f1",
            fontSize: "0.8rem",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
                backgroundColor: "rgba(1, 162, 241, 0.1)",
            }
        },
        inputArea: {
            padding: "15px",
            backgroundColor: "white",
            borderTop: "1px solid rgba(1, 162, 241, 0.1)",
            display: isMinimized ? "none" : "flex",
            alignItems: "center",
            gap: "10px"
        },
        // Mobile input area - add bottom safe area padding
        mobileInputArea: {
            padding: "15px",
            paddingBottom: "25px", // Extra padding for bottom of screen
            backgroundColor: "white",
            borderTop: "1px solid rgba(1, 162, 241, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "10px"
        },
        input: {
            flex: 1,
            padding: "12px 15px",
            border: "1px solid rgba(1, 162, 241, 0.2)",
            outline: "none",
            fontSize: "0.95rem",
            transition: "all 0.2s ease",
            "&:focus": {
                borderColor: "#01a2f1",
                boxShadow: "0px 0px 5px rgba(1, 162, 241, 0.1)"
            }
        },
        sendButton: {
            backgroundColor: "#01a2f1",
            color: "white",
            border: "none",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
                backgroundColor: "#0088d1",
            },
            "&:disabled": {
                backgroundColor: "#ccc",
                cursor: "not-allowed",
            }
        },
        typingIndicator: {
            color: "#666",
            fontSize: "0.9rem",
            padding: "8px 12px",
            backgroundColor: "rgba(1, 162, 241, 0.1)",
            display: "inline-flex",
            alignItems: "center",
            gap: "8px"
        },
        typingAnimation: {
            display: 'inline-block',
            width: '20px',
            textAlign: 'left'
        },
        typingDot: {
            display: 'inline-block',
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            backgroundColor: '#666',
            marginRight: '3px',
            animation: 'typingDot 1.4s infinite ease-in-out'
        },
        retryButton: {
            backgroundColor: "transparent",
            color: "#01a2f1",
            border: "1px solid #01a2f1",
            padding: "6px 12px",
            fontSize: "0.8rem",
            cursor: "pointer",
            transition: "all 0.2s ease",
            marginTop: "8px",
            "&:hover": {
                backgroundColor: "rgba(1, 162, 241, 0.1)",
            }
        },
        dragHandle: {
            position: 'absolute',
            top: '50%',
            left: '0',
            width: '10px',
            height: '30px',
            transform: 'translateY(-50%)',
            cursor: 'grab',
            backgroundColor: 'rgba(1, 162, 241, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isDragging ? 1 : 0,
            transition: 'opacity 0.2s ease'
        },
        quickResponsesContainer: {
            display: "flex",
            flexWrap: "wrap",
            padding: "0 0 10px 46px"
        }
    };

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, streamingMessage]);

    // Reset error state when opening chat
    useEffect(() => {
        if (isOpen) {
            setIsError(false);
        }
    }, [isOpen]);

    const handleRetry = () => {
        setIsError(false);
        // Remove the last error message if it exists
        setMessages(prev => {
            const lastMessage = prev[prev.length - 1];
            if (lastMessage.sender === "bot" && lastMessage.error) {
                return prev.slice(0, -1);
            }
            return prev;
        });
    };

    // Toggle minimized state
    const toggleMinimized = (e) => {
        e.stopPropagation();
        setIsMinimized(!isMinimized);
    };

    // Helper function to convert status codes to user-friendly messages
    const getStatusMessage = (status) => {
        switch (status) {
            case 'processing':
                return "Processing your request...";
            case 'fetching_context':
                return "Searching my knowledge base...";
            case 'generating':
                return "Generating response...";
            default:
                return "Thinking...";
        }
    };

    // Extended quick response buttons
    const quickResponses = [
        "How do I book an event?",
        "Payment options?", 
        "Cancellation policy?",
        "How do I sign up as talent?",
        "What types of events can I host?"
    ];

    const handleQuickResponse = (response) => {
        setInput(response);
        inputRef.current?.focus();
    };

    // Function for sending messages
    const sendStreamingMessage = () => {
        if (!input.trim()) return;
        
        // Store input locally to prevent closure issues
        const currentInput = input.trim();
        const now = new Date().toISOString();
        const userMessage = { text: currentInput, sender: "user", timestamp: now };
        
        // Add user message to chat
        setMessages(prev => [...prev, userMessage]);
        setInput("");
        setIsStreaming(true);
        setStreamingMessage("");
        setProcessingStatus("Processing...");
        setIsError(false);
        
        // Close any existing stream
        if (streamSource.current) {
            streamSource.current.close();
            streamSource.current = null;
        }
        
        // Use fetch for POST request with proper CORS headers
        fetch(`${API_BASE_URL}/api/chatbot`, {
            method: "POST",
            headers: { 
                "Content-Type": "application/json",
                "Origin": window.location.origin
            },
            mode: "cors",
            credentials: "include",
            body: JSON.stringify({
                message: currentInput,
                userId: userIdRef.current,
                userType: currentUser?.userType || "visitor"
            })
        })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Server responded with ${response.status}`);
            }
            return response.json();
        })
        .then(data => {
            // Add bot response to chat
            const botReply = { 
                text: data.reply || "Sorry, I don't have an answer for that.", 
                sender: "bot", 
                timestamp: new Date().toISOString() 
            };
            setMessages(prev => [...prev, botReply]);
            setIsStreaming(false);
        })
        .catch(error => {
            console.error("Chat Error:", error);
            setMessages(prev => [...prev, { 
                text: "⚠️ I'm having trouble connecting to my brain right now. Please try again in a moment.", 
                sender: "bot",
                error: true,
                timestamp: new Date().toISOString()
            }]);
            setIsError(true);
            setIsStreaming(false);
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (!isStreaming && input.trim()) {
                sendStreamingMessage();
            }
        }
    };

    // Utility function to group messages by sender for improved UI
    const groupMessagesBySender = () => {
        const groupedMessages = [];
        let currentGroup = null;
        
        messages.forEach((msg, index) => {
            // Start a new group when sender changes or this is the first message
            if (!currentGroup || currentGroup.sender !== msg.sender) {
                if (currentGroup) {
                    groupedMessages.push(currentGroup);
                }
                currentGroup = {
                    sender: msg.sender,
                    messages: [msg],
                    id: index
                };
            } else {
                currentGroup.messages.push(msg);
            }
        });
        
        // Add the last group
        if (currentGroup) {
            groupedMessages.push(currentGroup);
        }
        
        return groupedMessages;
    };

    // MOBILE VERSION - No floating button, only full-screen when triggered
    if (isMobile) {
        // Don't render anything if not open on mobile
        if (!isOpen) {
            return null;
        }
        
        return (
            <div style={styles.mobileChatWindow}>
                        <div style={styles.mobileHeader}>
                            <div style={styles.headerLeft}>
                                <img 
                                    src={IconChatbot} 
                                    alt="" 
                                    aria-hidden="true"
                                    style={styles.headerIcon}
                                />
                                <span>GiGBot Assistant</span>
                            </div>
                            <div style={styles.headerRight}>
                                <button 
                                    onClick={handleMobileClose}
                                    style={styles.closeButton}
                                    aria-label="Close chat"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        <div 
                            style={styles.messagesContainer}
                            aria-live="polite"
                            role="log"
                        >
                            {groupMessagesBySender().map((group) => (
                                <div
                                    key={group.id}
                                    style={{
                                        ...styles.messageGroup,
                                        alignItems: group.sender === "user" ? "flex-end" : "flex-start"
                                    }}
                                >
                                    {group.messages.map((msg, i) => (
                                        <div
                                            key={`${group.id}-${i}`}
                                            style={{
                                                ...styles.message,
                                                justifyContent: msg.sender === "user" ? "flex-end" : "flex-start",
                                                ...messageAnimation
                                            }}
                                        >
                                            {msg.sender === "bot" && i === 0 && (
                                                <img 
                                                    src={IconChatbot} 
                                                    alt="GiGBot" 
                                                    style={styles.botIcon}
                                                />
                                            )}
                                            {msg.sender === "bot" && i !== 0 && (
                                                <div style={{ width: '36px', flexShrink: 0 }}></div>
                                            )}
                                            <div
                                                style={
                                                    msg.sender === "user" 
                                                        ? styles.userMessage 
                                                        : msg.error 
                                                            ? styles.errorMessage 
                                                            : styles.botMessage
                                                }
                                            >
                                                {msg.error && <AlertTriangle size={16} />}
                                                {msg.text}
                                                <div style={styles.timestamp}>
                                                    {formatTime(msg.timestamp)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {/* Quick responses after bot messages */}
                                    {group.sender === "bot" && group.id === groupMessagesBySender().length - 1 && !isStreaming && (
                                        <div style={styles.quickResponsesContainer}>
                                            {quickResponses.map((response, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => handleQuickResponse(response)}
                                                    style={styles.quickResponse}
                                                >
                                                    {response}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Streaming message */}
                            {isStreaming && (
                                <div 
                                    style={{
                                        ...styles.message,
                                        justifyContent: "flex-start",
                                        ...messageAnimation
                                    }}
                                >
                                    <img 
                                        src={IconChatbot} 
                                        alt="GiGBot" 
                                        style={styles.botIcon}
                                    />
                                    <div style={styles.botMessage}>
                                        {streamingMessage || (
                                            <div style={styles.typingIndicator}>
                                            {processingStatus}
                                            <span style={styles.typingAnimation}>
                                                <span style={{...styles.typingDot, animationDelay: '0s'}}></span>
                                                <span style={{...styles.typingDot, animationDelay: '0.2s'}}></span>
                                                <span style={{...styles.typingDot, animationDelay: '0.4s'}}></span>
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {isError && (
                            <div style={{ 
                                textAlign: 'center', 
                                marginTop: '10px', 
                                marginBottom: '15px'
                            }}>
                                <button 
                                    onClick={handleRetry} 
                                    style={{
                                        ...styles.retryButton,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px'
                                    }}
                                >
                                    Try again
                                </button>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div style={styles.mobileInputArea}>
                        <input
                            type="text"
                            ref={inputRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Type your message..."
                            style={{...styles.input, borderColor: isStreaming ? "#ccc" : "rgba(1, 162, 241, 0.2)"}}
                            disabled={isStreaming}
                            aria-label="Message input"
                        />
                        <button
                            onClick={sendStreamingMessage}
                            disabled={isStreaming || !input.trim()}
                            style={{
                                ...styles.sendButton,
                                backgroundColor: (!input.trim() || isStreaming) ? "#ccc" : "#01a2f1",
                                boxShadow: (!input.trim() || isStreaming) ? "none" : "0px 2px 5px rgba(1, 162, 241, 0.3)"
                            }}
                            title="Send message"
                            aria-label="Send message"
                        >
                            <Send size={20} />
                        </button>
                    </div>
            </div>
        );
    }

// DESKTOP VERSION (unchanged from original)
return (
    <div 
        ref={containerRef}
        style={{
            ...styles.container,
            bottom: `${position.bottom}px`,
            right: `${position.right}px`
        }}
        role="dialog"
        aria-label="GiGBot Chat Assistant"
    >
        <div 
            style={styles.toggleContainer}
            onPointerDown={handlePointerDown}
            aria-hidden="true"
        >
            <button
                onClick={() => !isDragging && setIsOpen(!isOpen)}
                style={styles.toggleButton}
                onMouseEnter={e => {
                    if (!isDragging) {
                        const icon = e.currentTarget.querySelector('img');
                        if (icon) icon.style.transform = 'scale(1.1)';
                    }
                }}
                onMouseLeave={e => {
                    const icon = e.currentTarget.querySelector('img');
                    if (icon) icon.style.transform = 'scale(1)';
                }}
                aria-label={isOpen ? "Close chatbot" : "Open chatbot"}
                aria-expanded={isOpen}
            >
                <div style={styles.toggleButtonInner}>
                    {isOpen ? (
                        <X size={28} color="white" />
                    ) : (
                        <img 
                            src={IconChatbot} 
                            alt="ChatBot" 
                            style={styles.chatbotIcon}
                        />
                    )}
                </div>
            </button>
        </div>

        {isOpen && (
            <div style={styles.chatWindow}>
                <div 
                    style={styles.header}
                    onPointerDown={handlePointerDown}
                    aria-label="Draggable chat header"
                >
                    <div style={styles.headerLeft}>
                        <img 
                            src={IconChatbot} 
                            alt="" 
                            aria-hidden="true"
                            style={styles.headerIcon}
                        />
                        <span>GiGBot Assistant</span>
                    </div>
                    <div style={styles.headerRight}>
                        <button 
                            onClick={toggleMinimized}
                            style={styles.headerButton}
                            aria-label={isMinimized ? "Maximize chat window" : "Minimize chat window"}
                        >
                            {isMinimized ? (
                                <Maximize size={16} />
                            ) : (
                                <Minimize size={16} />
                            )}
                        </button>
                        <button 
                            onClick={() => setIsOpen(false)}
                            style={styles.closeButton}
                            aria-label="Close chat"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>

                <div 
                    style={styles.messagesContainer}
                    aria-live="polite"
                    role="log"
                >
                    {groupMessagesBySender().map((group) => (
                        <div
                            key={group.id}
                            style={{
                                ...styles.messageGroup,
                                alignItems: group.sender === "user" ? "flex-end" : "flex-start"
                            }}
                        >
                            {group.messages.map((msg, i) => (
                                <div
                                    key={`${group.id}-${i}`}
                                    style={{
                                        ...styles.message,
                                        justifyContent: msg.sender === "user" ? "flex-end" : "flex-start",
                                        ...messageAnimation
                                    }}
                                >
                                    {msg.sender === "bot" && i === 0 && (
                                        <img 
                                            src={IconChatbot} 
                                            alt="GiGBot" 
                                            style={styles.botIcon}
                                        />
                                    )}
                                    {msg.sender === "bot" && i !== 0 && (
                                        <div style={{ width: '36px', flexShrink: 0 }}></div>
                                    )}
                                    <div
                                        style={
                                            msg.sender === "user" 
                                                ? styles.userMessage 
                                                : msg.error 
                                                    ? styles.errorMessage 
                                                    : styles.botMessage
                                        }
                                    >
                                        {msg.error && <AlertTriangle size={16} />}
                                        {msg.text}
                                        <div style={styles.timestamp}>
                                            {formatTime(msg.timestamp)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            
                            {/* Quick responses after bot messages */}
                            {group.sender === "bot" && group.id === groupMessagesBySender().length - 1 && !isStreaming && (
                                <div style={styles.quickResponsesContainer}>
                                    {quickResponses.map((response, i) => (
                                        <button
                                            key={i}
                                            onClick={() => handleQuickResponse(response)}
                                            style={styles.quickResponse}
                                        >
                                            {response}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Streaming message */}
                    {isStreaming && (
                        <div 
                            style={{
                                ...styles.message,
                                justifyContent: "flex-start",
                                ...messageAnimation
                            }}
                        >
                            <img 
                                src={IconChatbot} 
                                alt="GiGBot" 
                                style={styles.botIcon}
                            />
                            <div style={styles.botMessage}>
                                {streamingMessage || (
                                    <div style={styles.typingIndicator}>
                                        {processingStatus}
                                        <span style={styles.typingAnimation}>
                                            <span style={{...styles.typingDot, animationDelay: '0s'}}></span>
                                            <span style={{...styles.typingDot, animationDelay: '0.2s'}}></span>
                                            <span style={{...styles.typingDot, animationDelay: '0.4s'}}></span>
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {isError && (
                        <div style={{ 
                            textAlign: 'center', 
                            marginTop: '10px', 
                            marginBottom: '15px'
                        }}>
                            <button 
                                onClick={handleRetry} 
                                style={{
                                    ...styles.retryButton,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                }}
                            >
                                Try again
                            </button>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <div style={styles.inputArea}>
                    <input
                        type="text"
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type your message..."
                        style={{...styles.input, borderColor: isStreaming ? "#ccc" : "rgba(1, 162, 241, 0.2)"}}
                        disabled={isStreaming}
                        aria-label="Message input"
                    />
                    <button
                        onClick={sendStreamingMessage}
                        disabled={isStreaming || !input.trim()}
                        style={{
                            ...styles.sendButton,
                            backgroundColor: (!input.trim() || isStreaming) ? "#ccc" : "#01a2f1",
                            boxShadow: (!input.trim() || isStreaming) ? "none" : "0px 2px 5px rgba(1, 162, 241, 0.3)"
                        }}
                        title="Send message"
                        aria-label="Send message"
                    >
                        <Send size={20} />
                    </button>
                </div>
            </div>
        )}

        <style>
            {`
                @keyframes fadeInSlide {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes pulse {
                    0% { opacity: 0.7; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.05); }
                    100% { opacity: 0.7; transform: scale(1); }
                }

                @keyframes typingDot {
                    0%, 60%, 100% { transform: translateY(0); }
                    30% { transform: translateY(-4px); }
                }

                /* Improve focus indicators for accessibility */
                button:focus-visible, input:focus-visible {
                    outline: 2px solid #01a2f1;
                    outline-offset: 2px;
                }
                
                /* Provide visual feedback for dragging */
                .dragging {
                    cursor: grabbing !important;
                    opacity: 0.9;
                }
                
                /* Better tap targets for mobile */
                @media (pointer: coarse) {
                    button {
                        min-height: 44px;
                        min-width: 44px;
                    }
                }
            `}
        </style>
    </div>
);
};

export default ChatBot;